import type { FeatureName } from "@lib/featureFlipper";

import type { NavigationRoutingRegistryType } from "../navigationRouting";
import { navigablePathRequiredFeatureFlippers } from "../navigationRouting";
import { isFeatureNOTObject } from "./isFeatureNOTObject";
import { isFeatureORObject } from "./isFeatureORObject";
import { isFeatureString } from "./isFeatureString";

/**
 * This method is used to determine if a React path should be included in the crossroads map.
 * All feature flippers defined in the `componentFeatureFlippers` property of the NRO must be enabled
 * for the path to be included in the map.
 *
 * @deprecated This method is used for backwards compatibility with legacy scenes.
 *
 * @param path The path to check
 * @param featureOn The feature flipper helper method
 * @returns boolean indicating if the path should be included in the crossroads map
 */
export const reactRoutingFeatureFlipperHelper = (
  path: keyof NavigationRoutingRegistryType,
  featureOn: (name: FeatureName) => boolean
): boolean => {
  // get the required feature flippers for the path
  const features = navigablePathRequiredFeatureFlippers[path];

  // if nothing is defined for the path, return false since
  // it is not a navigable path via the React router
  if (features === undefined) {
    return false;
  }

  // if there are no required feature flippers, return true
  // since the path is not gated by any feature flippers
  if (!features.length) {
    return true;
  }

  // check if all the required feature flippers are enabled
  // if they are, return true to include the path in the map
  // otherwise, return false to exclude the path from the map
  return features.every((feature) => {
    // if the feature flipper is a string, check if it is enabled
    if (isFeatureString(feature)) {
      return featureOn(feature);
    }

    // if the feature flipper is an object with an OR property,
    // check if any of the OR feature flippers are enabled
    if (isFeatureORObject(feature)) {
      return feature.OR.some(featureOn);
    }

    // if the feature flipper is an object with a NOT property,
    // check if all of the NOT feature flippers are disabled
    if (isFeatureNOTObject(feature)) {
      return !feature.NOT.every(featureOn);
    }

    throw new Error(`Invalid feature flipper '${feature}' for NRO path '${path}'`);
  });
};
